import React, { createContext, useState, useContext, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useColorScheme } from "@mui/material/styles";
import { authService, notificationService } from "../services/api";
import {
  initializeTokenRefresh,
  stopTokenRefresh,
  scheduleTokenRefresh,
} from "../services/tokenManager";
import {
  generateDeviceName,
  setDeviceName,
  clearDeviceName,
} from "../utils/push";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [collaborator, setCollaborator] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const { setMode } = useColorScheme();

  const clearLocalStorage = () => {
    clearDeviceName();
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("collaborator");
    localStorage.removeItem("customer");
    localStorage.removeItem("businesses");
  };

  // Feature-flag settings exposed by microservices (currently just
  // notification_service), namespaced by service. Fetched whenever there's
  // an authenticated user - on fresh login as well as on a restored session
  // - and kept fresh by react-query's own refetch, instead of a one-shot
  // fetch cached in localStorage.
  const { data: settings = {} } = useQuery({
    queryKey: ["settings"],
    queryFn: () =>
      notificationService
        .getSettings()
        .then((response) => ({ notification: response.data })),
    enabled: !!user,
    staleTime: 5 * 60_000,
    refetchInterval: 5 * 60_000,
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    checkAuth();

    // Initialize automatic token refresh
    initializeTokenRefresh();

    // Cleanup on unmount
    return () => {
      stopTokenRefresh();
    };
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem("access_token");

    if (!token) {
      setLoading(false);
      return;
    }

    try {
      // Load persisted context
      const storedCollaborator = localStorage.getItem("collaborator");
      const storedCustomer = localStorage.getItem("customer");
      const storedBusinesses = localStorage.getItem("businesses");

      if (storedCollaborator) {
        const collaboratorData = JSON.parse(storedCollaborator);
        setCollaborator(collaboratorData);

        // Reconstruct user from collaborator
        setUser({
          uuid: collaboratorData.uuid,
          name: collaboratorData.name,
          email: collaboratorData.email,
        });
      }

      if (storedCustomer) setCustomer(JSON.parse(storedCustomer));
      if (storedBusinesses) setBusinesses(JSON.parse(storedBusinesses));
    } catch (error) {
      console.error("Auth check failed:", error);
      clearLocalStorage();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      // Limpar cookies de sessão antes do login
      document.cookie.split(";").forEach((c) => {
        document.cookie = c
          .replace(/^ +/, "")
          .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
      });

      const response = await authService.login(email, password);
      const { access, refresh } = response.data;

      localStorage.setItem("access_token", access);
      localStorage.setItem("refresh_token", refresh);

      // Schedule automatic token refresh
      scheduleTokenRefresh(access);

      // CRITICAL: Bootstrap Customer Context
      // This endpoint DOES NOT require X-Customer-ID
      // This is the ONLY call after login - no /auth/users/me/
      try {
        const collaboratorResponse = await authService.getCollaboratorMe();
        const collaboratorData = collaboratorResponse.data;

        // Generate a device name and persist it on a cookie so the backend can
        // identify this device when push subscriptions are registered.
        setDeviceName(generateDeviceName(collaboratorData?.uuid));

        // Set user from collaborator data
        setUser({
          uuid: collaboratorData.uuid,
          name: collaboratorData.name,
          email: collaboratorData.email,
        });

        // Persist collaborator
        setCollaborator(collaboratorData);
        localStorage.setItem("collaborator", JSON.stringify(collaboratorData));

        // Color scheme saved on the account wins over this device's choice;
        // blank means never chosen, so the device keeps its own.
        const savedColorScheme = collaboratorData.preferences?.color_scheme;
        if (savedColorScheme) setMode(savedColorScheme);

        // Persist customer (used for X-Customer-ID header)
        if (collaboratorData.customer) {
          setCustomer(collaboratorData.customer);
          localStorage.setItem(
            "customer",
            JSON.stringify(collaboratorData.customer),
          );
        }

        // Persist businesses
        if (collaboratorData.businesses) {
          setBusinesses(collaboratorData.businesses);
          localStorage.setItem(
            "businesses",
            JSON.stringify(collaboratorData.businesses),
          );
        }
      } catch (collaboratorError) {
        console.error("Failed to fetch collaborator data:", collaboratorError);
        // If collaborator fetch fails, login should fail
        throw collaboratorError;
      }

      return { success: true };
    } catch (error) {
      console.error("Login error:", error);

      let errorMessage = "login.loginError";

      if (error.response?.status === 400 || error.response?.status === 401) {
        errorMessage = "login.invalidCredentials";
      } else if (error.response?.status === 403) {
        errorMessage = "You do not have access to this customer.";
      }

      return {
        success: false,
        error: errorMessage,
      };
    }
  };

  // Merges a partial update (e.g. from the self-service "update me" endpoint)
  // into the collaborator/user state and persists it, so every consumer
  // (this page, UserAvatar, etc.) reflects the change immediately.
  const updateCollaborator = (patch) => {
    setCollaborator((prev) => {
      const next = { ...prev, ...patch };
      localStorage.setItem("collaborator", JSON.stringify(next));
      return next;
    });

    if (patch.name !== undefined || patch.email !== undefined) {
      setUser((prev) =>
        prev
          ? {
              ...prev,
              ...(patch.name !== undefined && { name: patch.name }),
              ...(patch.email !== undefined && { email: patch.email }),
            }
          : prev,
      );
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      // Ignora erros de logout no backend
    } finally {
      // Stop automatic token refresh
      stopTokenRefresh();

      clearLocalStorage();
      setUser(null);
      setCollaborator(null);
      setCustomer(null);
      setBusinesses([]);
    }
  };

  // Internal (Beworks) user that isn't a collaborator of any customer: it
  // only has access to customer management in the dashboard.
  const isInternalOnly = Boolean(collaborator?.is_internal && !collaborator?.customer);

  const value = {
    user,
    collaborator,
    isInternalOnly,
    customer,
    businesses,
    settings,
    login,
    logout,
    updateCollaborator,
    loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
